import React, { useEffect, useRef, useState } from "react";

type Props = React.ImgHTMLAttributes<HTMLImageElement> & { src: string };

// `onError` rides through in `...rest`; it is named here only because EB-518 depends on it: the
// card asks for a thumbnail that may not exist yet and swaps back to the original when it 404s.

export default function LazyImage({ src, alt, ...rest }: Props) {
  const [inView, setInView] = useState(false);
  const ref = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setInView(true)),
      { rootMargin: "200px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // `decoding="async"` keeps image decode off the main thread. It matters more as the grid grows:
  // several cards can scroll into view at once, and synchronous decodes block scrolling.
  return (
    <img
      ref={ref}
      alt={alt}
      loading="lazy"
      decoding="async"
      src={inView ? src : undefined}
      {...rest}
    />
  );
}
